/**
 * S6.2 batch-capture API calls — slice-local thin service.
 *
 * Fronts the same `/shipment-batches` mock endpoints as `shipping.api.ts`
 * but with the Board 5 capture payloads (mode/櫃型/最後交期日/批號/Sample 資訊
 * on create; 箱數/箱號/材積/備註 per line at cut-off) plus the 改數量
 * quantity-correction loop. Lives beside the shipping components because the
 * shared `src/services/api/shipping.api.ts` is owned by a parallel slice —
 * fold these calls into it when that seam opens (slice follow-up).
 */

import apiClient from '@/services/api/axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import type { ContainerType, ShipmentBatchCapture, ShipmentMode } from './batchCapture';

export interface CreateCapturedBatchRequest {
  vesselOrCarrier: string;
  etd: string;
  eta: string;
  poIds: string[];
  mode: ShipmentMode;
  containerType: ContainerType | null;
  finalDeliveryDate: string | null;
  lotNumber: string | null;
  sampleInfo: string | null;
}

export interface CutOffCapturedLineInput {
  id: string;
  qtyLoaded: number;
  cbm?: number;
  grossWeightKg?: number;
  cartonCount?: number | null;
  cartonFrom?: number | null;
  cartonTo?: number | null;
  note?: string | null;
}

export interface CutOffCapturedBatchRequest {
  containerNumber: string;
  sealNumber: string;
  lines: CutOffCapturedLineInput[];
}

export interface CorrectBatchQuantitiesRequest {
  /** Mandatory business reason — logged on the batch correction trail. */
  reason: string;
  lines: Array<{ id: string; qtyLoaded: number }>;
}

export const batchCaptureApi = {
  createBatch: async (
    body: CreateCapturedBatchRequest
  ): Promise<{ data: ShipmentBatchCapture }> => {
    const { data } = await apiClient.post<{ data: ShipmentBatchCapture }>(
      API_ROUTES.SHIPMENT_BATCHES,
      body
    );
    return data;
  },

  /** 封櫃截單 with the loading capture grid (server gates unchanged). */
  cutOffBatch: async (
    id: string,
    body: CutOffCapturedBatchRequest
  ): Promise<{ data: ShipmentBatchCapture }> => {
    const { data } = await apiClient.post<{ data: ShipmentBatchCapture }>(
      `${API_ROUTES.SHIPMENT_BATCHES}/${id}/cut-off`,
      body
    );
    return data;
  },

  /**
   * 改數量 — the low-privilege correction loop on a sealed batch. Server
   * enforces: sealed status, 0 ≤ qty ≤ planned, never below already-received,
   * reason required; short-load exceptions re-derive from the corrected facts.
   */
  correctQuantities: async (
    id: string,
    body: CorrectBatchQuantitiesRequest
  ): Promise<{ data: ShipmentBatchCapture }> => {
    const { data } = await apiClient.post<{ data: ShipmentBatchCapture }>(
      `${API_ROUTES.SHIPMENT_BATCHES}/${id}/quantity-corrections`,
      body
    );
    return data;
  },
};

export default batchCaptureApi;
